import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// react admin
import {
  Datagrid,
  NumberField,
  List,
  DateField,
  TextField,
  ReferenceField,
  TopToolbar,
  FunctionField,
  useNotify,
  useUpdate,
  useRefresh,
  useStore,
  useListContext,
  useListController,
  useGetIdentity,
} from "react-admin";
// mui
import {
  Chip,
  Switch,
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Select,
  InputLabel,
  FormControl,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
// mui icon
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import BackupTableIcon from "@mui/icons-material/BackupTable";
// component
import CustomPagination from "../../Components/CustomPagination";
import { NewUserFormDialog } from "./forms/NewUserForm";
import { RechargeBalanceFormDialog } from "./forms/CreateTransaction";
import { CreateDialogButton } from "../../Components/CreateDialogueButton";
import { ActionsMenu, ActionsMenuItem } from "../../Components/ActionsMenu";
import SendMessageButton from "../Messaging/SendMessageButton";
import { RegistrationSummary } from "./Summary/RegistrationSummary";
import { RedeemBalanceFormDialog } from "./forms/RedeemTransaction";
import { ResetPasswordFormDialog } from "./forms/ResetPassword";
import { DeleteUsers } from "./dialogs/DeleteUsers";
import { MarketingBanner } from "../../Components/MarketingBanner";
// loader
import { Loader } from "../Loader";
// utils
import { userListExports } from "../../Utilities/allExports";
// xlsx export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { currentPageUsers } from "../../Provider/parseDataProvider";
import {
  initializeWebSocket,
  sendWebSocketMessage,
} from "../../Provider/socket";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import Parse from "parse";
import Autocomplete from "@mui/material/Autocomplete";
import MessagingPanel from "./messaging/MessagingPanel";

const TestUserSwitch = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    try {
      const result = await Parse.Cloud.run("toggleTestUser", {
        userId: record.id,
        isTesting: !record.isTesting,
      });

      notify(result.message || "Test user status updated", { type: "success" });
      refresh();
    } catch (error) {
      notify(error.message || "Failed to update test user status", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Switch
        checked={record.isTesting === true}
        onChange={handleToggle}
        disabled={loading}
        size="large"
      />
      {loading && <CircularProgress size={20} />}
    </>
  );
};

const BooleanSwitch = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [registrationSummaryCall, setRegistrationSummaryCall] = useStore(
    "registrationSummaryCall"
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [loading, setLoading] = useState(false);

  const isUpperLevel = record?.roleName !== "Player";

  const handleToggle = useCallback(() => {
    if (isUpperLevel) {
      setPendingToggle(!record.isActive);
      setConfirmOpen(true);
    } else {
      proceedToggle(!record.isActive);
    }
  }, [record]);

  const proceedToggle = async (newIsActive) => {
    setLoading(true);
    try {
      await Parse.Cloud.run("toggleUserAndDownline", {
        userId: record.id,
        isActive: newIsActive,
      });

      try {
        sendWebSocketMessage(
          "message",
          JSON.stringify({
            eventName: "user_status",
            isActive: newIsActive,
            userId: record.id,
          })
        );
      } catch (socketError) {
        console.error("WebSocket emit failed:", socketError);
        notify("User status updated but socket failed", { type: "warning" });
      }

      notify("User status updated successfully", { type: "info" });
      refresh();
      setRegistrationSummaryCall(true);
    } catch (err) {
      notify(err.message || "Failed to update user status", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmProceed = () => {
    setConfirmOpen(false);
    if (pendingToggle !== null) {
      proceedToggle(pendingToggle);
      setPendingToggle(null);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setPendingToggle(null);
  };

  return (
    <>
      <Switch
        checked={record.isActive}
        onChange={handleToggle}
        color="primary"
      />
      {loading && <Loader />}

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmCancel}
        PaperProps={{
          sx: {
            backgroundColor: "#1f1f1f",
            color: "#fff",
            borderRadius: 2,
            minWidth: 400,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: "bold" }}>
          Confirm Status Change
        </DialogTitle>

        <DialogContent sx={{ mb: 2 }}>
          <Typography>
            {pendingToggle === false ? (
              <>
                This will <strong>deactivate</strong> the user and{" "}
                <strong>all of their associated accounts</strong>. Would you
                like to proceed?
              </>
            ) : (
              <>
                This will <strong>reactivate</strong> the user and{" "}
                <strong>all of their associated accounts</strong>. Would you
                like to proceed?
              </>
            )}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button onClick={handleConfirmCancel} sx={{ color: "#ccc" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            sx={{
              backgroundColor: "#00BCD4",
              color: "#000",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#00ACC1",
              },
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CustomFilter = () => {
  const { setFilters } = useListContext();

  const [selectedField, setSelectedField] = useState("username");
  const [filterValue, setFilterValue] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // 🔍 Search managers (excluding Player role)
  const handleSearch = async (inputValue) => {
    if (!inputValue) {
      setUserOptions([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const query = new Parse.Query(Parse.User);
      query.limit(20);
      query.select(["username", "role"]);
      query.include("role");
      query.matches("username", inputValue, "i"); // case-insensitive

      // exclude Player role
      const roleQuery = new Parse.Query(Parse.Role);
      roleQuery.notEqualTo("name", "Player");
      query.matchesQuery("role", roleQuery);

      const results = await query.find();
      const users = results.map((u) => ({
        id: u.id,
        username: u.get("username"),
        role: u.get("role")?.get("name") || "Unknown",
      }));
      setUserOptions(users);
    } catch (err) {
      console.error("User search failed:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleApply = () => {
    if (!filterValue || filterValue === "") return;
    setFilters({ [selectedField]: filterValue }, {});
  };

  const handleClear = () => {
    setSelectedField("username");
    setFilterValue("");
    setFilters({}, {});
    setUserOptions([]);
  };

  return (
    <Box
      display={{ xs: "block", width: "100%", sm: "flex" }}
      alignItems="center"
      gap={2}
    >
      <Box display="flex" width="max-content">
        <FormControl>
          <InputLabel>Filter By</InputLabel>
          <Select
            size="small"
            value={selectedField}
            label="Search By"
            onChange={(e) => {
              setSelectedField(e.target.value);
              setFilterValue("");
            }}
            sx={{ marginRight: "10px", minWidth: 120 }}
          >
            <MenuItem value="username">Username</MenuItem>
            <MenuItem value="publicEmail">Email</MenuItem>
            <MenuItem value="manager">Role</MenuItem>
          </Select>
        </FormControl>

        {selectedField === "manager" ? (
          <Autocomplete
            size="small"
            sx={{ minWidth: 220 }}
            options={userOptions}
            getOptionLabel={(option) => `${option.username} (${option.role})`}
            loading={loadingUsers}
            onInputChange={(e, newValue) => handleSearch(newValue)}
            onChange={(e, newValue) =>
              setFilterValue(newValue ? newValue.id : "")
            }
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label="Search Manager"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : (
          <MuiTextField
            size="small"
            variant="outlined"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder="Enter value"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
          />
        )}
      </Box>

      <Box display="flex">
        <Button
          variant="contained"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": { backgroundColor: "#FFA000" },
          }}
          onClick={handleApply}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            outline: "1px solid #FFC107",
            color: "white",
            textTransform: "none",
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default CustomFilter;

const UserChild = ({ setFilters }) => {
  const { filterValues } = useListContext();

  // Pass filters to parent when they change
  useEffect(() => {
    setFilters(filterValues);
  }, [filterValues, setFilters]);
};

export const UserList = () => {
  const { isLoading, total, page, perPage, setPage, setPerPage } =
    useListController();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: userIdentity } = useGetIdentity();
  const isSuperUser = userIdentity?.userRoleName === "Super-User";
  const [isExporting, setIsExporting] = useState(false);
  const [canMarkTestUsers, setCanMarkTestUsers] = useState(false);

  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();

  useEffect(() => {
    const socket = initializeWebSocket((data) => {
      if (data.eventName === "updatedBalance") {
        if (currentPageUsers.has(data?.userId)) {
          console.log("User is in current page, triggering refresh");
          refresh();
        }
      } else if (data.event === "error") {
        console.log("*** error", data.message);
      }
    });
  }, [refresh]);

  // Check test user permission
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const result = await Parse.Cloud.run("getTestUserPermission");
        setCanMarkTestUsers(result.canMarkTestUsers);
      } catch (error) {
        console.error("Failed to check test user permission:", error);
      }
    };

    if (userIdentity) {
      checkPermission();
    }
  }, [userIdentity]);

  const currentUserData = localStorage.getItem(
    `Parse/${process.env.REACT_APP_APP_ID}/currentUser`
  );

  const parsedUserData = JSON.parse(currentUserData);

  if (!currentUserData) {
    navigate("/login");
  }

  const [selectedRecord, setSelectedRecord] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filters, setFilters] = useState({});

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // export the data in pdf formate
  const handleExportPDF = async () => {
    console.log("*** Generating PDF with filters:", filters);
  };

  // export the data in xls formate
  const handleExportXLS = async () => {
    try {
      setIsExporting(true);
      const exportData = await userListExports(filters);

      if (!exportData || !exportData?.data || exportData?.data?.length === 0) {
        console.warn("No data to export.");
        return;
      }

      const selectedFields = exportData.data.map((item) => ({
        Active: item.isActive,
        Username: item.username,
        Name: item.name,
        Email: item.email,
        Phone: item.phone,
        DeviceID: item.deviceID,
        Role: item.role.name,
        Balance: item.balance,
        RegisterDate: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false, // Set to true for AM/PM format
        }).format(new Date(item.createdAt)),
        Manager: item.manager.name,
      }));

      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User Lists");
      const xlsData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([xlsData], { type: "application/octet-stream" }),
        "UserLists.xlsx"
      );
    } catch (error) {
      console.error("Error fetching user engagement:", error);
    } finally {
      setIsExporting(false);
      handleMenuClose();
    }
  };

  const listActions = (
    <TopToolbar>
      <CreateDialogButton>
        Add New User
        <NewUserFormDialog />
      </CreateDialogButton>
      <Button
        variant="contained"
        startIcon={<GetAppIcon />}
        sx={{
          marginLeft: "10px",
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        }}
        onClick={handleMenuOpen}
      >
        Export
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ marginTop: 1 }}
      >
        {/* <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            PDF file
          </Typography>
        </MenuItem> */}

        <MenuItem onClick={handleExportXLS} disabled={isExporting}>
          <ListItemIcon>
            <BackupTableIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {isExporting ? "Exporting..." : "Excel file"}
          </Typography>
        </MenuItem>
      </Menu>
    </TopToolbar>
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          minHeight: "100vh",
          pb: 4,
        }}
      >
        {!isSuperUser && (
          <Box sx={{ pt: 2, pb: 2 }}>
            <MarketingBanner />
          </Box>
        )}
        <Box
          sx={{
            mt: 4,
            backgroundColor: "#242424",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        ><MessagingPanel /></Box>
        <Box
          sx={{
            mt: 2,
            backgroundColor: "#242424",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >             
          <RegistrationSummary />
        </Box>
        <Box sx={{ pt: 2 }}>
          <Box
            sx={{
              mt: 2,
              backgroundColor: "#242424",
              borderRadius: 2,
              boxShadow: "none !important",
              padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
            }}
          >
            <List
              title="User Management"
              filters={<CustomFilter />}
              actions={listActions}
              pagination={false}
              emptyWhileLoading={true}
              empty={false}
              sort={{ field: "createdAt", order: "DESC" }}
              sx={{
                "& .RaList-actions": {
                  flexWrap: "nowrap",
                },
                "& .RaFilterFormInput-spacer": { display: "none" },
              }}
            >
              <UserChild setFilters={setFilters} />
              <Box
                style={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Box
                  style={{
                    width: "100%",
                    position: isSmallScreen ? "absolute" : "relative",
                  }}
                >
                  <Datagrid
                    bulkActionButtons={false}
                    sx={{
                      overflowX: "auto",
                      overflowY: "hidden",
                      width: "100%",
                      maxHeight: "100%",
                      "& .RaDatagrid-table": {
                        width: "100%",
                      },
                    }}
                  >
                    <FunctionField
                      label="Actions"
                      render={(record) => {
                        const currentUserData = JSON.parse(
                          localStorage.getItem(
                            `Parse/${process.env.REACT_APP_APP_ID}/currentUser`
                          )
                        );

                        // Hide actions menu for logged-in user's row
                        if (
                          !record ||
                          record.id === currentUserData?.objectId
                        ) {
                          return null;
                        }

                        return (
                          <ActionsMenu label="Actions">
                            {/* Send Message Button - First option */}
                            <SendMessageButton record={record} />
                            
                            {!["Player", "Agent"].includes(
                              record?.roleName
                            ) && (
                              <>
                                <ActionsMenuItem
                                  record={record}
                                  onClick={(rec) =>
                                    navigate(`/users/${record.id}`, {
                                      state: rec,
                                    })
                                  }
                                >
                                  Hierarchy
                                </ActionsMenuItem>
                              </>
                            )}
                            {record.isActive &&
                              record.manager?.id ===
                                currentUserData?.objectId && (
                                <>
                                  <ActionsMenuItem>
                                    Redeem
                                    <RedeemBalanceFormDialog />
                                  </ActionsMenuItem>
                                  <ActionsMenuItem>
                                    Recharge
                                    <RechargeBalanceFormDialog />
                                  </ActionsMenuItem>
                                </>
                              )}
                            {/* <ActionsMenuItem>
                        Edit Info
                        <EditUserFormDialog1 />
                      </ActionsMenuItem> */}

                            <ActionsMenuItem>
                              Reset Password
                              <ResetPasswordFormDialog />
                            </ActionsMenuItem>
                            {record?.roleName === "Player" && (
                              <>
                                {/* <ActionsMenuItem
                              record={record}
                              onClick={(rec) =>
                                navigate(`/users/${record.id}`, {
                                  state: rec,
                                })
                              }
                            >
                              Wining Data
                            </ActionsMenuItem> */}
                                {/* <ActionsMenuItem>
                              Delete
                              <DeleteUsers />
                            </ActionsMenuItem> */}
                              </>
                            )}

                            {record?.roleName === "Player" && (
                              <ActionsMenuItem
                                onClick={() =>
                                  navigate(
                                    `/user-details/${record?.id}?username=${record?.username}`
                                  )
                                }
                              >
                                Game Details
                              </ActionsMenuItem>
                            )}
                            {isSuperUser &&
                            <ActionsMenuItem
                              onClick={() =>
                                navigate(
                                  `/user-status-history/${record.id}?username=${record.username}`
                                )
                              }
                            >
                              Status History
                            </ActionsMenuItem>}
                            {isSuperUser && record?.roleName === "Game-Owner" && (
                              <ActionsMenuItem
                                onClick={async () => {
                                  try {
                                    const currentPermission = record.canMarkTestUsers === true;
                                    await Parse.Cloud.run("updateTestUserPermission", {
                                      targetUserId: record.id,
                                      canMarkTestUsers: !currentPermission,
                                    });
                                    refresh();
                                  } catch (error) {
                                    console.error("Failed to update permission:", error);
                                  }
                                }}
                              >
                                {record.canMarkTestUsers ? "Revoke" : "Grant"} Test User Permission
                              </ActionsMenuItem>
                            )}
                          </ActionsMenu>
                        );
                      }}
                    />
                    {parsedUserData?.objectId && (
                      <FunctionField
                        label="Status"
                        render={(record) =>
                          parsedUserData?.objectId !== record.id ? (
                            <BooleanSwitch record={record} />
                          ) : (
                            <></>
                          )
                        }
                      />
                    )}
                    {canMarkTestUsers && !isSuperUser && (
                      <FunctionField
                        label="Test User"
                        render={(record) => {
                          // Only show for Player role
                          if (record?.roleName !== "Player") {
                            return null;
                          }
                          return <TestUserSwitch record={record} />;
                        }}
                      />
                    )}
                    <TextField source="username" label="Username" />
                    <TextField source="publicEmail" label="Email" />
                    <TextField source="phone" label="Phone" />
                    <ReferenceField
                      source="role.id"
                      reference="roles"
                      label="Role"
                      emptyText="Missing Role"
                      link={false}
                      sortBy="role.rolename"
                    >
                      <FunctionField
                        render={(record) => {
                          const getColor = (status) => {
                            switch (status) {
                              case "Super-User":
                                return "primary";
                              case "Game-Owner":
                                return "primary";
                              case "Master-Distributor":
                                return "secondary";
                              case "Distributor":
                                return "error";
                              case "Sub-Distributor":
                                return "warning";
                              case "Agent":
                                return "info";
                              case "Player":
                                return "success";
                              default:
                                return "default";
                            }
                          };
                          return (
                            <Chip
                              label={record.name}
                              color={getColor(record.name)}
                              size="small"
                              variant="outlined"
                            />
                          );
                        }}
                      />
                    </ReferenceField>
                    <NumberField source="balance" textAlign="left" />
                    <DateField
                      source="createdAt"
                      label="Register date"
                      showTime
                    />
                    <ReferenceField
                      source="manager.id"
                      reference="users"
                      label="Manager"
                      emptyText="Missing User"
                      link={false}
                      sortBy="manager.username"
                    >
                      <TextField source="username" />
                    </ReferenceField>
                  </Datagrid>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100% !important",
                      margin: "16px 0px",
                    }}
                  >
                    <CustomPagination
                      page={page}
                      perPage={perPage}
                      total={total}
                      setPage={setPage}
                      setPerPage={setPerPage}
                    />
                  </Box>
                </Box>
              </Box>
            </List>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};
